import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Box, Tab, Typography, CircularProgress, Button } from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import BreadcrumbsNav from "components/BreadcrumbsNav";
import { useSingleJourney } from "./queries/useJourneyQuery";
import EmptyJourney from "./components/EmptyJourney";
import styles from "./styles.module.scss";
import TestJourneyTab from "./components/TestJourneyTab";
import OverviewTab from "./components/OverviewTab";
import TestCasesTab from "./components/TestCasesTab";
import { InfoOutlined } from "@mui/icons-material";
import clsx from "clsx";
import { useNotification } from "context/NotificationContext";
import TacticIcon from "assets/iconComponents/TacticIcon";
import { socketEventEmitter } from "socket";
import { journeyQueryKeys } from "./queries/keys";
import {
  useRegenerateTestCaseMutation,
  useGenerateTestCasesMutation,
} from "./components/TestCasesTab/queries";
import CreateTestCaseModal from "./components/TestCasesTab/components/CreateTestCaseModal";
import { JOURNEY_STATUS } from "constants/journey";

const TABS = {
  journey: "journey",
  testCases: "testCases",
  overview: "overview",
};

const isValidTab = (v) =>
  v === TABS.journey || v === TABS.testCases || v === TABS.overview;

const SingleJourneyContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const showNotification = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    data: journey,
    isLoading: isJourneyLoading,
    isError,
  } = useSingleJourney(id);

  const {
    mutateAsync: regenerateTestCasesAsync,
    isPending: isRegenerationLoading,
  } = useRegenerateTestCaseMutation(journey?.id);

  const { mutateAsync: generateTestCasesAsync, isPending: isMutationPending } =
    useGenerateTestCasesMutation(journey?.id);

  const isGeneration =
    isMutationPending ||
    isRegenerationLoading ||
    journey?.status === JOURNEY_STATUS.in_progress.value;

  const hasSteps = Array.isArray(journey?.steps) && journey.steps.length > 0;
  const isManualMetadata =
    !journey?.metadata || journey?.metadata?.source === "manual";
  const isGenerateDisabled = !hasSteps || isManualMetadata;

  const activeTab = useMemo(() => {
    const fromUrl = searchParams.get("tab");
    if (isValidTab(fromUrl)) return fromUrl;
    return TABS.journey;
  }, [searchParams]);

  useEffect(() => {
    const fromUrl = searchParams.get("tab");
    if (isValidTab(fromUrl)) return;

    const requested = location.state?.activeTab;
    if (!isValidTab(requested)) return;

    setSearchParams(
      (prev) => {
        prev.set("tab", requested);
        return prev;
      },
      { replace: true }
    );
  }, [location.state, searchParams, setSearchParams]);

  const [isInfoBar, setIsInfoBar] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("generate");
  const [regenerationOptions, setRegenerationOptions] = useState(null);

  const handleIsInfoBar = (showed) => {
    setIsInfoBar(showed);
  };

  useEffect(() => {
    const handleJourneyUpdate = (payload) => {
      if (!payload) return;
      const journeyId = payload.id || payload.journeyId;
      if (journeyId && journeyId === id) {
        queryClient.invalidateQueries({
          queryKey: [journeyQueryKeys.singleJourney, id],
        });
      }
    };
    socketEventEmitter.on("journey:updated", handleJourneyUpdate);
    return () => {
      socketEventEmitter.off("journey:updated", handleJourneyUpdate);
    };
  }, [id, queryClient]);

  useEffect(() => {
    if (!id) return;
    const handleTaskUpdate = (payload) => {
      if (!payload) return;
      const journeyId = payload.journeyId;
      if (journeyId && journeyId === id) {
        queryClient.invalidateQueries({
          queryKey: [journeyQueryKeys.singleJourney, id],
        });
      }
    };
    socketEventEmitter.on("taskCompleted", handleTaskUpdate);
    socketEventEmitter.on("taskActivated", handleTaskUpdate);
    socketEventEmitter.on("taskFailed", handleTaskUpdate);
    return () => {
      socketEventEmitter.off("taskCompleted", handleTaskUpdate);
      socketEventEmitter.off("taskFailed", handleTaskUpdate);
      socketEventEmitter.off("taskActivated", handleTaskUpdate);
    };
  }, [id, queryClient]);

  const handleChangeActiveTab = (_, value) => {
    setSearchParams(
      (prev) => {
        prev.set("tab", value);
        return prev;
      },
      { replace: false }
    );
  };

  const openGenerateModal = useCallback(() => {
    setModalMode("generate");
    setRegenerationOptions(null);
    setIsCreateModalOpen(true);
  }, []);

  const openRegenerateModal = useCallback((options = {}) => {
    setModalMode("regenerate");
    setRegenerationOptions({
      idsToRegenerate: options.idsToRegenerate,
      regenerateAll: options.regenerateAll,
    });
    setIsCreateModalOpen(true);
  }, []);

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    setRegenerationOptions(null);
  }, []);

  const activeTabColor = (isActive) => {
    if (isActive) return "#4574D2";
    return "rgba(0, 0, 0, 0.6)";
  };

  const handleGenerate = async (contextData = null) => {
    try {
      await generateTestCasesAsync(contextData);
      showNotification("Journey test case generation has started", "info", "Learn more", navigate);
    } catch (error) {
      showNotification(
        `Error! ${error?.response?.data?.message || "Something went wrong"}`,
        "error"
      );
    }
  };

  const handleRegenerate = async (contextData = null) => {
    try {
      await regenerateTestCasesAsync({
        idsToRegenerate: regenerationOptions?.idsToRegenerate,
        regenerateAll: regenerationOptions?.regenerateAll,
        contextData: contextData || null,
      });
      showNotification("Journey test case generation has started", "info", "Learn more", navigate);
    } catch (error) {
      showNotification(
        `Error! ${error?.response?.data?.message || "Something went wrong"}`,
        "error"
      );
    }
  };

  if (isJourneyLoading)
    return (
      <Box className={styles.loaderWrapper}>
        <CircularProgress />
      </Box>
    );

  if (isError || !journey) return <EmptyJourney />;

  return (
    <Box
      className={`${styles.testJourneyContentContainer} singleJourneyContent`}
    >
      <BreadcrumbsNav pageLabel="journey" pageName={journey.label} />
      <Box className={styles.testJourneyTopSection}>
        <Box
          className={clsx(
            styles.titleWrapper,
            isInfoBar ? styles.titleWrapperWithInfoBar : ""
          )}
        >
          <Box className={styles.leftContainer}>
            <Box
              className={styles.testJourneyBackButton}
              onClick={() => navigate("/test-journeys")}
            >
              <ArrowBackIosIcon />
            </Box>
            <Typography className={styles.testJourneyTitle}>
              {journey.label}
            </Typography>
          </Box>
          {activeTab === TABS.journey ? (
            <Box className={styles.headerActions}>
              <Button
                className={styles.headerGenerateButton}
                color="secondary"
                variant="contained"
                onClick={openGenerateModal}
                disabled={isGeneration || isGenerateDisabled}
              >
                {isGeneration ? "GENERATING..." : "GENERATE TEST CASES"}
              </Button>
            </Box>
          ) : null}
        </Box>
      </Box>

      <TabContext value={activeTab}>
        <Box className={styles.testJourneyTogglerContainer}>
          <TabList
            className={styles.testJourneyTabsToggler}
            onChange={handleChangeActiveTab}
          >
            <Tab
              className={styles.testJourneyTabLabelContainer}
              label={
                <Box
                  className={styles.testJourneyTabLabel}
                  sx={{ color: activeTabColor(activeTab === TABS.journey) }}
                >
                  <TacticIcon
                    className={styles.testJourneyTabLabelIcon}
                    fill={activeTabColor(activeTab === TABS.journey)}
                  />
                  <span className={styles.testJourneyTabLabelText}>
                    TEST JOURNEY
                  </span>
                </Box>
              }
              value={TABS.journey}
            />
            <Tab
              className={styles.testJourneyTabLabelContainer}
              label={
                <Box
                  className={styles.testJourneyTabLabel}
                  sx={{ color: activeTabColor(activeTab === TABS.testCases) }}
                >
                  <ListAltOutlinedIcon
                    className={styles.testJourneyTabLabelIcon}
                    color={activeTabColor(activeTab === TABS.testCases)}
                  />
                  <span className={styles.testJourneyTabLabelText}>
                    TEST CASES
                  </span>
                </Box>
              }
              value={TABS.testCases}
            />
            <Tab
              className={styles.testJourneyTabLabelContainer}
              label={
                <Box
                  className={styles.testJourneyTabLabel}
                  sx={{ color: activeTabColor(activeTab === TABS.overview) }}
                >
                  <InfoOutlined
                    className={styles.testJourneyTabLabelIcon}
                    color={activeTabColor(activeTab === TABS.overview)}
                  />
                  <span className={styles.testJourneyTabLabelText}>
                    OVERVIEW
                  </span>
                </Box>
              }
              value={TABS.overview}
            />
          </TabList>
        </Box>

        <TabPanel
          value={TABS.journey}
          className={activeTab === TABS.journey ? styles.tabPanel : ""}
        >
          <TestJourneyTab
            steps={journey.steps}
            journeyId={journey.id}
            pageBlocksDiscoveryDebug={journey.page_blocks_discovery_debug}
            handleInfoBar={handleIsInfoBar}
          />
        </TabPanel>

        <TabPanel
          value={TABS.testCases}
          className={activeTab === TABS.testCases ? styles.tabPanel : ""}
        >
          <TestCasesTab
            journeyId={journey.id}
            onOpenGenerateModal={openGenerateModal}
            onOpenRegenerateModal={openRegenerateModal}
            isGeneration={isGeneration}
            isGenerateDisabled={isGenerateDisabled}
          />
        </TabPanel>

        <TabPanel
          value={TABS.overview}
          className={activeTab === TABS.overview ? styles.tabPanel : ""}
        >
          <OverviewTab
            journeyId={journey.id}
            requirements={journey.user_requirements}
            expectedResult={journey.user_expected_test_cases}
          />
        </TabPanel>
      </TabContext>

      <CreateTestCaseModal
        open={isCreateModalOpen}
        onClose={closeCreateModal}
        journeyId={journey.id}
        userRequirements={journey.user_requirements || ""}
        userExpectedTestCases={journey.user_expected_test_cases || ""}
        mode={modalMode}
        onGenerate={
          modalMode === "generate" ? handleGenerate : handleRegenerate
        }
        isGeneration={isGeneration}
      />
    </Box>
  );
};

export default SingleJourneyContent;
